import React, { useState, useEffect } from 'react';
import { Star, Gift, Users, Copy, Check, ChevronDown, ChevronUp, Share2, Award, Clock, ArrowRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PointsSection = ({ user }) => {
  const { theme } = useTheme();
  const [pointsData, setPointsData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [showRewards, setShowRewards] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [applyingCode, setApplyingCode] = useState(false);
  const [copied, setCopied] = useState(false);
  const [message, setMessage] = useState(null);
  const [confirmRedeem, setConfirmRedeem] = useState(null);
  const [redemptions, setRedemptions] = useState([]);

  useEffect(() => { fetchAll(); }, [user]);

  const fetchAll = async () => {
    try {
      const [ptsRes, settingsRes, redemptionsRes] = await Promise.all([
        axios.get(`${API}/user/points`, { withCredentials: true }),
        axios.get(`${API}/points-settings`),
        axios.get(`${API}/user/redemptions`, { withCredentials: true })
      ]);
      setPointsData(ptsRes.data);
      setSettings(settingsRes.data);
      setRedemptions(redemptionsRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleCopy = () => {
    if (pointsData?.referral_code) {
      navigator.clipboard.writeText(pointsData.referral_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShare = () => {
    const code = pointsData?.referral_code;
    if (!code) return;
    const msg = `Comprá en *Bebida Bacana* y ganá puntos. Usá mi código *${code}* para recibir puntos de bienvenida.\n${window.location.origin}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const handleApplyReferral = async () => {
    if (!referralCode.trim()) return;
    setApplyingCode(true);
    setMessage(null);
    try {
      const res = await axios.post(`${API}/user/apply-referral`, { referral_code: referralCode }, { withCredentials: true });
      setMessage({ type: 'success', text: res.data.message });
      setReferralCode('');
      fetchAll();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Error al aplicar código' });
    } finally { setApplyingCode(false); }
  };

  const handleRedeem = async (rewardId) => {
    setMessage(null);
    try {
      const res = await axios.post(`${API}/user/points/redeem`, { reward_id: rewardId }, { withCredentials: true });
      setMessage({ type: 'success', text: res.data.message });
      setConfirmRedeem(null);
      fetchAll();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Error al canjear' });
      setConfirmRedeem(null);
    }
  };

  if (loading) return <div className="text-center py-6 text-slate-400 text-sm">Cargando...</div>;
  if (!settings?.enabled) return null;

  const points = pointsData?.points || 0;
  const totalEarned = pointsData?.total_earned || 0;
  const rewards = settings?.rewards?.filter(r => r.active) || [];
  const history = pointsData?.history || [];
  const myCode = pointsData?.referral_code || '';
  const referralEnabled = settings?.referral?.enabled !== false;

  return (
    <div className="space-y-4" data-testid="points-section">
      {/* Points Balance Card */}
      <div className="rounded-2xl p-5 text-white" style={{ background: `linear-gradient(135deg, ${theme.primary} 0%, #065F46 100%)` }} data-testid="points-balance-card">
        <div className="flex items-center gap-2 mb-1">
          <Star size={18} className="text-amber-300" />
          <span className="text-sm font-medium text-white/80">Mis Puntos</span>
        </div>
        <p className="text-4xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>{points}</p>
        <p className="text-xs text-white/60 mt-1">Total acumulados: {totalEarned} | Cada ${(settings?.pesos_per_point || 10000).toLocaleString('es-AR')} = 1 punto</p>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-3 rounded-xl text-sm font-medium ${message.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
          {message.text}
        </div>
      )}

      {/* Active Redemptions */}
      {redemptions.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">Premios activos</p>
          {redemptions.map(r => (
            <div key={r.id} className="flex items-center justify-between py-2 border-b border-amber-100 last:border-0">
              <div className="flex items-center gap-2">
                <Gift size={14} className="text-amber-500" />
                <span className="text-sm font-medium text-amber-800">{r.reward_name}</span>
              </div>
              <span className="text-xs text-amber-500">Pendiente de uso</span>
            </div>
          ))}
          <p className="text-[10px] text-amber-500 mt-2">Mencioná tu premio al hacer el próximo pedido</p>
        </div>
      )}

      {/* Rewards */}
      {rewards.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
          <button onClick={() => setShowRewards(!showRewards)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors" data-testid="toggle-rewards">
            <div className="flex items-center gap-2">
              <Gift size={16} className="text-amber-500" />
              <span className="text-sm font-semibold text-slate-700">Canjear Puntos</span>
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">{rewards.length}</span>
            </div>
            {showRewards ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
          </button>
          {showRewards && (
            <div className="px-4 pb-4 space-y-2">
              {rewards.map(r => {
                const canAfford = points >= r.points_cost;
                return (
                  <div key={r.id} className={`p-3 rounded-xl border transition-all ${canAfford ? 'border-slate-200 hover:border-slate-300' : 'border-slate-100 opacity-60'}`} data-testid={`reward-${r.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{r.name}</p>
                        <p className="text-xs text-slate-400">
                          {r.reward_type === 'discount_percent' && `${r.reward_value}% de descuento`}
                          {r.reward_type === 'discount_amount' && `$${r.reward_value.toLocaleString('es-AR')} de descuento`}
                          {r.reward_type === 'product' && 'Producto gratis'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold" style={{ color: canAfford ? theme.primary : '#94A3B8' }}>{r.points_cost} pts</p>
                        {confirmRedeem === r.id ? (
                          <div className="flex gap-1 mt-1">
                            <button onClick={() => handleRedeem(r.id)} className="px-2 py-0.5 text-[10px] font-bold bg-emerald-600 text-white rounded-lg" data-testid={`confirm-redeem-${r.id}`}>SI</button>
                            <button onClick={() => setConfirmRedeem(null)} className="px-2 py-0.5 text-[10px] font-medium border border-slate-300 rounded-lg">No</button>
                          </div>
                        ) : canAfford ? (
                          <button onClick={() => setConfirmRedeem(r.id)} className="text-[10px] font-semibold px-3 py-1 mt-1 rounded-lg text-white transition-opacity hover:opacity-90" style={{ backgroundColor: theme.primary }} data-testid={`redeem-${r.id}`}>
                            Canjear
                          </button>
                        ) : (
                          <p className="text-[10px] text-slate-400">Faltan {r.points_cost - points} pts</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Referral Section */}
      {referralEnabled && (
        <div className="bg-white rounded-xl border border-slate-100 p-4 space-y-3" data-testid="referral-section">
          <div className="flex items-center gap-2">
            <Users size={16} className="text-indigo-500" />
            <span className="text-sm font-semibold text-slate-700">Programa de Referidos</span>
          </div>
          
          {/* My code */}
          {myCode && (
            <div>
              <p className="text-xs text-slate-500 mb-1.5">Tu código para compartir:</p>
              <div className="flex gap-2">
                <div className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 flex items-center justify-between">
                  <span className="text-base font-bold tracking-wider text-slate-800" style={{ fontFamily: 'Barlow Condensed' }} data-testid="my-referral-code">{myCode}</span>
                  <button onClick={handleCopy} className="ml-2 text-slate-400 hover:text-slate-600 transition-colors" data-testid="copy-referral-code">
                    {copied ? <Check size={16} className="text-emerald-500" /> : <Copy size={16} />}
                  </button>
                </div>
                <button onClick={handleShare} className="px-3 rounded-xl bg-emerald-500 text-white hover:bg-emerald-600 transition-colors" title="Compartir por WhatsApp" data-testid="share-referral-wa">
                  <Share2 size={16} />
                </button>
              </div>
              {pointsData?.referral_count > 0 && (
                <p className="text-xs text-emerald-600 font-medium mt-1.5">
                  <Award size={12} className="inline mr-1" />{pointsData.referral_count} persona{pointsData.referral_count > 1 ? 's' : ''} referida{pointsData.referral_count > 1 ? 's' : ''}
                </p>
              )}
            </div>
          )}

          {/* Apply referral code */}
          {!pointsData?.referred_by && (
            <div>
              <p className="text-xs text-slate-500 mb-1.5">¿Tenés un código de referido?</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  placeholder="Ingresá el código"
                  className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:border-slate-300 outline-none uppercase tracking-wider"
                  maxLength={12}
                  data-testid="referral-code-input"
                />
                <button onClick={handleApplyReferral} disabled={applyingCode || !referralCode.trim()}
                  className="px-4 py-2 text-sm font-semibold text-white rounded-xl disabled:opacity-40 transition-opacity hover:opacity-90"
                  style={{ backgroundColor: theme.primary }} data-testid="apply-referral-btn">
                  {applyingCode ? '...' : 'Aplicar'}
                </button>
              </div>
              <p className="text-[10px] text-slate-400 mt-1">Ganá {settings?.referral?.referred_points || 3} puntos de bienvenida</p>
            </div>
          )}
          {pointsData?.referred_by && (
            <p className="text-xs text-emerald-600 flex items-center gap-1"><Check size={12} /> Referido por código: {pointsData.referred_by}</p>
          )}
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
          <button onClick={() => setShowHistory(!showHistory)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors" data-testid="toggle-history">
            <div className="flex items-center gap-2">
              <Clock size={16} className="text-slate-400" />
              <span className="text-sm font-semibold text-slate-700">Historial</span>
            </div>
            {showHistory ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
          </button>
          {showHistory && (
            <div className="px-4 pb-3 space-y-1.5">
              {[...history].reverse().slice(0, 15).map((entry, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                  <div>
                    <p className="text-xs text-slate-600">{entry.description || entry.type}</p>
                    <p className="text-[10px] text-slate-400">{new Date(entry.date).toLocaleDateString('es-AR')}</p>
                  </div>
                  <span className={`text-sm font-bold ${entry.points > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {entry.points > 0 ? '+' : ''}{entry.points}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PointsSection;
